import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import RecoveryLayout from '../../components/RecoveryLayout';
import PageLoader from '../../components/PageLoader';
import PaidReceiptsImportModal from '../../components/PaidReceiptsImportModal';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';

const STATUS_COLORS = {
  pending:   { bg: '#fee2e2', color: '#991b1b' },
  paid:      { bg: '#dcfce7', color: '#166534' },
  change:    { bg: '#fef3c7', color: '#92400e' },
  informed:  { bg: '#e0f2fe', color: '#0c4a6e' },
  confirmed: { bg: '#ede9fe', color: '#5b21b6' },
  'No Answer': { bg: '#e5e7eb', color: '#374151' },
};

const STATUS_OPTIONS = ['all', 'pending', 'paid', 'change', 'informed', 'confirmed', 'No Answer'];

// Badge colors (pill inside student cell)
const REG_STATUS_COLORS = {
  active:       { bg: '#16a34a', color: '#fff'    },  // solid green
  pending:      { bg: '#d97706', color: '#fff'    },  // amber
  postponed:    { bg: '#0284c7', color: '#fff'    },  // sky blue
  suspending:   { bg: '#ea580c', color: '#fff'    },  // orange
  inactivating: { bg: '#dc2626', color: '#fff'    },  // red
  inactivated:  { bg: '#7f1d1d', color: '#fecaca' },  // dark red / light text
  suspended:    { bg: '#475569', color: '#f1f5f9' },  // slate / light text
};

const fmt = (value) => {

  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '').trim());
  if (Number.isNaN(parsed)) return '—';

  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(parsed);
};

const fmtShort = (value) => {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(n)) return '0';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n);
};

const formatDueDate = (value) => {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  parsed.setDate(parsed.getDate() + 1);

  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatMonthTitle = (value) => {
  if (!value) return 'All Months';

  const parsed = new Date(`${value}-01`);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });
};

const formatDateTime = (value) => {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getCopyableContactNo = (value) => {
  if (!value) return '';

  const contact = String(value).trim();
  if (!contact) return '';

  return contact.startsWith('0') && contact.length > 1 ? contact.slice(1) : contact;
};

const toInputDateValue = (value) => {
  if (!value) return '';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const RECEIPT_MATCH_BADGES = {
  'same-month': { label: 'Will Update', bg: '#dcfce7', color: '#166534', dot: '#16a34a' },
  'other-month': { label: 'New Month Row', bg: '#dbeafe', color: '#1e40af', dot: '#2563eb' },
  'no-match':    { label: 'New Student',   bg: '#f3e8ff', color: '#6b21a8', dot: '#9333ea' },
};

const RECEIPT_IMPORT_COLUMN_ALIASES = {
  receiptId: ['receipt id', 'receipt no', 'receipt number'],
  transactionDate: ['transaction date', 'paid date', 'date'],
  regId: ['registration id', 'registration no', 'reg id', 'reg no'],
  course: ['course'],
  student: ['student', 'student name'],
  paymentType: ['payment type', 'payment method', 'payment'],
  amount: ['amount', 'paid amount'],
};

const normalizeReceiptImportHeader = (value) => String(value || '')
  .replace(/&nbsp;/gi, ' ')
  .replace(/[().:_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

const getReceiptImportField = (header) => {
  const normalized = normalizeReceiptImportHeader(header);
  return Object.entries(RECEIPT_IMPORT_COLUMN_ALIASES).find(([, aliases]) => aliases.includes(normalized))?.[0] || null;
};

const cleanReceiptImportCell = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

const parseReceiptImportTable = (input) => {
  const source = String(input || '').trim();

  if (!source) {
    return { rows: [], error: 'Paste the receipt table HTML first.' };
  }

  let headers = [];
  let rawRows = [];

  if (/<table[\s>]|<tr[\s>]|<td[\s>]|<th[\s>]/i.test(source)) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(source, 'text/html');
    const table = doc.querySelector('table');

    if (!table) {
      return { rows: [], error: 'No HTML table was found in the pasted content.' };
    }

    const headerCells = Array.from(table.querySelectorAll('thead tr:first-child th, thead tr:first-child td'));
    headers = headerCells.map((cell) => cleanReceiptImportCell(cell.textContent));

    const rowElements = table.querySelectorAll('tbody tr').length
      ? Array.from(table.querySelectorAll('tbody tr'))
      : Array.from(table.querySelectorAll('tr')).slice(1);

    rawRows = rowElements.map((row) => Array.from(row.querySelectorAll('td, th')).map((cell) => cleanReceiptImportCell(cell.textContent)));
  } else {
    const lines = source.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (lines.length < 2) {
      return { rows: [], error: 'Paste an HTML table or tab-separated rows with a header line.' };
    }

    headers = lines[0].split('\t').map((cell) => cleanReceiptImportCell(cell));
    rawRows = lines.slice(1).map((line) => line.split('\t').map((cell) => cleanReceiptImportCell(cell)));
  }

  const fieldIndexes = headers.map((header) => getReceiptImportField(header));

  if (!fieldIndexes.some(Boolean)) {
    return { rows: [], error: 'The pasted table does not contain the required receipt columns.' };
  }

  const rows = rawRows
    .map((cells) => {
      const mapped = {
        receiptId: '',
        transactionDate: '',
        regId: '',
        course: '',
        student: '',
        paymentType: '',
        amount: '',
      };

      fieldIndexes.forEach((field, index) => {
        if (field) {
          mapped[field] = cleanReceiptImportCell(cells[index]);
        }
      });

      return mapped;
    })
    .filter((row) => Object.values(row).some(Boolean))
    .map((row, index) => {
      const errors = [];
      const normalizedAmount = row.amount ? row.amount.replace(/,/g, '').trim() : '';

      if (!row.regId) errors.push('Registration ID is required.');
      if (!row.transactionDate) errors.push('Transaction Date is required.');
      if (!normalizedAmount || Number.isNaN(Number(normalizedAmount))) errors.push('Amount must be a valid number.');

      return {
        rowNumber: index + 1,
        ...row,
        amount: normalizedAmount,
        errors,
      };
    });

  if (rows.length === 0) {
    return { rows: [], error: 'No receipt rows were found in the pasted content.' };
  }

  return { rows, error: '' };
};

export default function DuesList({ pageTitle, section }) {
  const isAdmin = (() => {
    const user = AuthService.getUser();
    const isSuperAdmin = Boolean(user?.superAdmin?.superAdminId);
    const role = String(
      user?.superAdmin?.staff?.role ||
      user?.superAdmin?.role ||
      user?.superAdmin?.staff?.staffType ||
      user?.superAdmin?.staff?.type ||
      ''
    ).trim().toLowerCase();
    return isSuperAdmin || role === 'admin' || role === 'administrator';
  })();

  const [records, setRecords]           = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [regStatusFilter, setRegStatusFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState([]);
  const [batchFilter, setBatchFilter]   = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [inlineError, setInlineError] = useState('');
  const [copiedContact, setCopiedContact] = useState('');
  const [followUpModal, setFollowUpModal] = useState({ isOpen: false, row: null });
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [followUpForm, setFollowUpForm] = useState({
    comment: '',
    nextReminderDate: '',
    nextReminderComment: '',
    submitting: false,
    error: '',
    loadingHistory: false,
  });
  const [receiptImportModal, setReceiptImportModal] = useState({
    isOpen: false,
    input: '',
    rows: [],
    error: '',
    submitting: false,
  });
  const [receiptImportFeedback, setReceiptImportFeedback] = useState(null);

  // --- Check Students Presence ---
  const [presenceModal, setPresenceModal] = useState({ isOpen: false, loading: false, data: null, error: '' });
  const [scheduleModal, setScheduleModal] = useState({ isOpen: false, loading: false, student: null, schedule: null, error: '' });
  const [presenceTab, setPresenceTab] = useState('summary'); // 'summary' | 'today' | 'floors'
  const [todayView, setTodayView] = useState('normal'); // 'normal' | 'floor' | 'course'
  const [expandedGroups, setExpandedGroups] = useState({}); // today grouped view collapse state
  const [expandedSessions, setExpandedSessions] = useState({}); // today normal session collapse state
  const [expandedFloors, setExpandedFloors] = useState({}); // floor wise tab collapse state

  const [dateRangeField, setDateRangeField] = useState('confirmDateToPay'); // 'dueDate' | 'confirmDateToPay'
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd]   = useState('');
  const [monthFilter, setMonthFilter]   = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = monthFilter ? `?month=${monthFilter}` : '';
        const resp = await axiosInstance.get(`/admin/recovery-dues${params}`);
        if (!cancelled) {
          setRecords(resp?.data?.dues ?? []);
          setAvailableMonths(resp?.data?.availableMonths ?? []);
        }
      } catch {
        if (!cancelled) setRecords([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [monthFilter, refreshKey]);

  const validReceiptImportRows = useMemo(() => (
    receiptImportModal.rows.filter((row) => row.errors.length === 0)
  ), [receiptImportModal.rows]);

  const blockedReceiptImportRows = useMemo(() => (
    receiptImportModal.rows.filter((row) => row.errors.length > 0)
  ), [receiptImportModal.rows]);

  const receiptImportSameMonth = useMemo(() => (
    validReceiptImportRows.filter((row) => row.matchStatus === 'same-month')
  ), [validReceiptImportRows]);

  const receiptImportOtherMonth = useMemo(() => (
    validReceiptImportRows.filter((row) => row.matchStatus === 'other-month')
  ), [validReceiptImportRows]);

  const receiptImportNoMatch = useMemo(() => (
    validReceiptImportRows.filter((row) => row.matchStatus === 'no-match')
  ), [validReceiptImportRows]);

  const receiptImportTotalAmount = useMemo(() => (
    validReceiptImportRows.reduce((sum, row) => {
      const val = parseFloat(row.amount);
      return sum + (Number.isNaN(val) ? 0 : val);
    }, 0)
  ), [validReceiptImportRows]);

  const courseOptions = useMemo(() => {
    const unique = [...new Set(records.map((r) => r.course).filter(Boolean))].sort();
    return unique.map((c) => ({ value: c, label: c }));
  }, [records]);

  const batchOptions = useMemo(() => {
    const unique = [...new Set(records.map((r) => r.batchName).filter(Boolean))].sort();
    return unique.map((b) => ({ value: b, label: b }));
  }, [records]);

  const filtered = records.filter((r) => {
    const matchSearch =
      !search ||
      String(r.studentName || '').toLowerCase().includes(search.toLowerCase()) ||
      String(r.studentId || '').toLowerCase().includes(search.toLowerCase()) ||
      String(r.batchName || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || r.dueStatus === statusFilter;
    const matchCourse = courseFilter.length === 0 || courseFilter.some((opt) => opt.value === r.course);
    const matchBatch  = !batchFilter  || r.batchName === batchFilter.value;
    const matchRegStatus = regStatusFilter === 'all' || (r.regStatus || '').toLowerCase() === regStatusFilter;
    let matchDateRange = true;
    if (dateRangeStart || dateRangeEnd) {
      const fieldVal = toInputDateValue(dateRangeField === 'dueDate' ? r.dueDate : r.confirmDateToPay);
      if (dateRangeStart && fieldVal < dateRangeStart) matchDateRange = false;
      if (dateRangeEnd   && fieldVal > dateRangeEnd)   matchDateRange = false;
      if (!fieldVal) matchDateRange = false;
    }
    return matchSearch && matchStatus && matchCourse && matchBatch && matchRegStatus && matchDateRange;
  });

  // Summary uses only course+batch scope — unaffected by search or status filter
  const summaryScope = useMemo(() => records.filter((r) => {
    const matchCourse = courseFilter.length === 0 || courseFilter.some((opt) => opt.value === r.course);
    const matchBatch  = !batchFilter  || r.batchName === batchFilter.value;
    return matchCourse && matchBatch;
  }), [records, courseFilter, batchFilter]);

  const summary = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    let totalDueAmount = 0;
    let totalPaidAmount = 0;
    let actualPaidAmount = 0;
    let extraPaidAmount = 0;
    let totalConfirmedAmount = 0;
    let todayConfirmedAmount = 0;
    const statusCounts = {};
    const regStatusCounts = {};

    summaryScope.forEach((r) => {
      totalDueAmount += parseFloat(r.dueAmount) || 0;
      if (r.dueStatus === 'paid') {
        const paid = parseFloat(r.confirmedAmount) || 0;
        totalPaidAmount += paid;
        // "Extra" rows = imported receipts with no full student info
        const isExtra = !r.contactNo || !r.regStatus || !r.regDate;
        if (isExtra) extraPaidAmount += paid;
        else actualPaidAmount += paid;
      }

      if (r.dueStatus === 'confirmed') {
        const conf = parseFloat(r.confirmedAmount);
        if (!Number.isNaN(conf)) {
          totalConfirmedAmount += conf;
          if (toInputDateValue(r.confirmDateToPay) === today) {
            todayConfirmedAmount += conf;
          }
        }
      }

      const status = r.dueStatus || 'pending';
      statusCounts[status] = (statusCounts[status] || 0) + 1;

      const regKey = (r.regStatus || '').toLowerCase();
      if (regKey) regStatusCounts[regKey] = (regStatusCounts[regKey] || 0) + 1;
    });

    return { total: summaryScope.length, totalDueAmount, totalPaidAmount, actualPaidAmount, extraPaidAmount, totalConfirmedAmount, todayConfirmedAmount, statusCounts, regStatusCounts };
  }, [summaryScope]);

  const handleSearchChange = (e) => { setSearch(e.target.value); };
  const handleStatusChange = (e) => { setStatusFilter(e.target.value); };
  const handleMonthChange  = (e) => { setMonthFilter(e.target.value); };

  const startEditingCell = (row, field) => {
    let nextValue = '';

    if (field === 'dueStatus') nextValue = row.dueStatus || 'pending';
    if (field === 'confirmedAmount') nextValue = row.confirmedAmount ?? '';
    if (field === 'confirmDateToPay') nextValue = toInputDateValue(row.confirmDateToPay);

    setInlineError('');
    setEditingCell({ dueId: row.dueId, field });
    setEditingValue(String(nextValue));
  };

  const stopEditingCell = () => {
    setEditingCell(null);
    setEditingValue('');
    setSavingCell(null);
    setInlineError('');
  };

  const updateRecordFields = (dueId, patch) => {
    setRecords((prev) => prev.map((record) => (
      record.dueId === dueId ? { ...record, ...patch } : record
    )));

    setFollowUpModal((prev) => {
      if (!prev.row || prev.row.dueId !== dueId) return prev;
      return { ...prev, row: { ...prev.row, ...patch } };
    });
  };

  const saveInlineEdit = async (row, field, rawValue) => {
    if (!row?.dueId) return;

    let payload = {};
    let nextPatch = {};

    if (field === 'dueStatus') {
      payload = { dueStatus: rawValue };
      nextPatch = { dueStatus: rawValue };
      if ((row.dueStatus || '') === rawValue) {
        stopEditingCell();
        return;
      }
    }

    if (field === 'confirmedAmount') {
      const normalized = String(rawValue).trim();
      const currentValue = row.confirmedAmount === null || row.confirmedAmount === undefined ? '' : String(row.confirmedAmount);
      if (normalized === currentValue) {
        stopEditingCell();
        return;
      }
      payload = { confirmedAmount: normalized || null };
      nextPatch = { confirmedAmount: normalized || null };
    }

    if (field === 'confirmDateToPay') {
      const normalized = String(rawValue).trim();
      const currentValue = toInputDateValue(row.confirmDateToPay);
      if (normalized === currentValue) {
        stopEditingCell();
        return;
      }
      payload = { confirmDateToPay: normalized || null };
      nextPatch = { confirmDateToPay: normalized || null };
    }

    try {
      setInlineError('');
      setSavingCell({ dueId: row.dueId, field });
      const response = await axiosInstance.patch(`/admin/recovery-dues/${row.dueId}`, payload);
      updateRecordFields(row.dueId, response?.data?.data || nextPatch);
      stopEditingCell();
    } catch (error) {
      const message = error?.response?.data?.errors?.[field]?.[0]
        || error?.response?.data?.message
        || 'Failed to update this field.';
      setSavingCell(null);
      setInlineError(message);
    }
  };

  const isEditingCell = (dueId, field) => editingCell?.dueId === dueId && editingCell?.field === field;
  const isSavingCell = (dueId, field) => savingCell?.dueId === dueId && savingCell?.field === field;

  const handleCopyContact = async (contactNo) => {
    const copyValue = getCopyableContactNo(contactNo);
    if (!copyValue || !navigator?.clipboard?.writeText) return;

    try {
      await navigator.clipboard.writeText(copyValue);
      setCopiedContact(contactNo);
      window.setTimeout(() => {
        setCopiedContact((current) => (current === contactNo ? '' : current));
      }, 1500);
    } catch {
      setCopiedContact('');
    }
  };

  const syncFollowUpCount = (dueId, count) => {
    setRecords((prev) => prev.map((record) => (
      record.dueId === dueId ? { ...record, followupCount: count } : record
    )));

    setFollowUpModal((prev) => {
      if (!prev.row || prev.row.dueId !== dueId) return prev;
      return { ...prev, row: { ...prev.row, followupCount: count } };
    });
  };

  const loadFollowUpHistory = async (dueId) => {
    if (!dueId) {
      setFollowUpHistory([]);
      return;
    }

    try {
      setFollowUpForm((prev) => ({ ...prev, loadingHistory: true, error: '' }));
      const res = await axiosInstance.get(`/admin/recovery-dues/${dueId}/followups`);
      const items = res?.data?.data ?? [];
      setFollowUpHistory(items);
      syncFollowUpCount(dueId, items.length);
    } catch {
      setFollowUpHistory([]);
      setFollowUpForm((prev) => ({ ...prev, error: 'Failed to load follow-up history.' }));
    } finally {
      setFollowUpForm((prev) => ({ ...prev, loadingHistory: false }));
    }
  };

  const openFollowUpModal = async (row) => {
    setFollowUpModal({ isOpen: true, row });
    setFollowUpHistory([]);
    setFollowUpForm({
      comment: '',
      nextReminderDate: '',
      nextReminderComment: '',
      submitting: false,
      error: '',
      loadingHistory: false,
    });
    await loadFollowUpHistory(row?.dueId);
  };

  const closeFollowUpModal = () => {
    setFollowUpModal({ isOpen: false, row: null });
    setFollowUpHistory([]);
    setFollowUpForm({
      comment: '',
      nextReminderDate: '',
      nextReminderComment: '',
      submitting: false,
      error: '',
      loadingHistory: false,
    });
  };

  const openReceiptImportModal = () => {
    setReceiptImportModal({
      isOpen: true,
      input: '',
      rows: [],
      error: '',
      submitting: false,
      inputCollapsed: false,
    });
    setReceiptImportFeedback(null);
  };

  const closeReceiptImportModal = () => {
    setReceiptImportModal({
      isOpen: false,
      input: '',
      rows: [],
      error: '',
      submitting: false,
      inputCollapsed: false,
    });
  };

  const previewReceiptImport = () => {
    const parsed = parseReceiptImportTable(receiptImportModal.input);

    // Build a cross-reference map from currently loaded records.
    // records.studentId is the Reg_ID column; records.reportMonth is 'YYYY-MM'.
    const recordsByRegId = {};
    records.forEach((r) => {
      const key = String(r.studentId || '').trim();
      if (!key) return;
      if (!recordsByRegId[key]) recordsByRegId[key] = [];
      recordsByRegId[key].push(r);
    });

    const enrichedRows = parsed.rows.map((row) => {
      const regIdKey = String(row.regId || '').trim();
      const transMonth = row.transactionDate ? row.transactionDate.slice(0, 7) : null;
      const matchingRecords = recordsByRegId[regIdKey] || [];

      let matchStatus = 'no-match';
      let matchedRecord = null;

      if (matchingRecords.length > 0) {
        const sameMonth = transMonth
          ? matchingRecords.find((r) => (r.reportMonth || '').slice(0, 7) === transMonth)
          : null;
        if (sameMonth) {
          matchStatus = 'same-month';
          matchedRecord = sameMonth;
        } else {
          matchStatus = 'other-month';
          // show the most recent loaded record as reference info
          matchedRecord = matchingRecords.sort((a, b) => (
            (b.reportMonth || '').localeCompare(a.reportMonth || '')
          ))[0];
        }
      }

      return { ...row, matchStatus, matchedRecord };
    });

    setReceiptImportModal((prev) => ({
      ...prev,
      rows: enrichedRows,
      error: parsed.error,
      inputCollapsed: enrichedRows.length > 0,
    }));
  };

  const submitReceiptImport = async () => {
    if (validReceiptImportRows.length === 0) {
      setReceiptImportModal((prev) => ({
        ...prev,
        error: prev.rows.length > 0
          ? 'Fix the blocked rows before importing.'
          : 'Preview the pasted table before importing.',
      }));
      return;
    }

    try {
      setReceiptImportModal((prev) => ({ ...prev, submitting: true, error: '' }));

      const payloadRows = validReceiptImportRows.map((row) => ({
        receiptId: row.receiptId,
        transactionDate: row.transactionDate,
        regId: row.regId,
        course: row.course,
        student: row.student,
        paymentType: row.paymentType,
        amount: row.amount,
      }));

      const response = await axiosInstance.post('/admin/recovery-dues/import-paid-receipts', {
        rows: payloadRows,
      });

      const data = response?.data || {};
      const hasIssues = (data.errors || 0) > 0 || (data.skipped || 0) > 0;

      setReceiptImportFeedback({
        type: hasIssues ? 'warning' : 'success',
        message: `${data.updated || 0} updated, ${data.inserted || 0} inserted, ${data.skipped || 0} skipped, ${data.errors || 0} errors.`,
      });

      closeReceiptImportModal();

      // Immediately refresh the dues list so stat cards (Paid Amount etc.) reflect
      // the import without showing a full-page loader.
      try {
        const params = monthFilter ? `?month=${monthFilter}` : '';
        const refreshed = await axiosInstance.get(`/admin/recovery-dues${params}`);
        setRecords(refreshed?.data?.dues ?? []);
        setAvailableMonths(refreshed?.data?.availableMonths ?? []);
      } catch (_) {
        // Fall back to a key-based reload if the direct fetch fails
        setRefreshKey((current) => current + 1);
      }
    } catch (error) {
      setReceiptImportModal((prev) => ({
        ...prev,
        submitting: false,
        error: error?.response?.data?.message || 'Failed to import paid receipts.',
      }));
    }
  };

  const handlePaidReceiptImportCompleted = async (result) => {
    setReceiptImportFeedback({
      type: result?.hasIssues ? 'warning' : 'success',
      message: `${result?.updated || 0} updated, ${result?.inserted || 0} inserted, ${result?.skipped || 0} skipped, ${result?.errors || 0} errors.`,
    });

    closeReceiptImportModal();

    try {
      const params = monthFilter ? `?month=${monthFilter}` : '';
      const refreshed = await axiosInstance.get(`/admin/recovery-dues${params}`);
      setRecords(refreshed?.data?.dues ?? []);
      setAvailableMonths(refreshed?.data?.availableMonths ?? []);
    } catch (_) {
      setRefreshKey((current) => current + 1);
    }
  };

  const submitFollowUp = async () => {
    if (!followUpModal.row?.dueId) return;

    const payload = {
      comment: followUpForm.comment.trim(),
      next_reminder_date: followUpForm.nextReminderDate.trim() || null,
      next_reminder_comment: followUpForm.nextReminderComment.trim() || null,
    };

    if (!payload.comment) {
      setFollowUpForm((prev) => ({ ...prev, error: 'Comment is required.' }));
      return;
    }

    try {
      setFollowUpForm((prev) => ({ ...prev, submitting: true, error: '' }));
      await axiosInstance.post(`/admin/recovery-dues/${followUpModal.row.dueId}/followups`, payload);
      await loadFollowUpHistory(followUpModal.row.dueId);
      setFollowUpForm({
        comment: '',
        nextReminderDate: '',
        nextReminderComment: '',
        submitting: false,
        error: '',
        loadingHistory: false,
      });
    } catch (error) {
      const message = error?.response?.data?.errors?.comment?.[0]
        || error?.response?.data?.message
        || 'Failed to save follow-up.';
      setFollowUpForm((prev) => ({ ...prev, submitting: false, error: message }));
    }
  };

  const titleMonthLabel = formatMonthTitle(monthFilter);

  const openPresenceModal = async () => {
    setPresenceTab('summary');
    setTodayView('normal');
    setExpandedGroups({});
    setExpandedSessions({});
    setExpandedFloors({});
    setPresenceModal({ isOpen: true, loading: true, data: null, error: '' });
    try {
      const params = monthFilter ? `?month=${monthFilter}` : '';
      const res = await axiosInstance.get(`/admin/recovery-dues/check-presence${params}`);
      setPresenceModal({ isOpen: true, loading: false, data: res.data, error: '' });
    } catch (err) {
      setPresenceModal({ isOpen: true, loading: false, data: null, error: err?.response?.data?.message || 'Failed to load presence data.' });
    }
  };

  const closePresenceModal = () => {
    setPresenceModal({ isOpen: false, loading: false, data: null, error: '' });
  };

  const openScheduleModal = async (student) => {
    setScheduleModal({ isOpen: true, loading: true, student, schedule: null, error: '' });
    try {
      const params = monthFilter ? `?month=${monthFilter}` : '';
      const res = await axiosInstance.get(`/admin/recovery-dues/check-presence/${encodeURIComponent(student.studentId)}/schedule${params}`);
      setScheduleModal({ isOpen: true, loading: false, student, schedule: res.data, error: '' });
    } catch (err) {
      setScheduleModal({ isOpen: true, loading: false, student, schedule: null, error: err?.response?.data?.message || 'Failed to load schedule.' });
    }
  };

  const closeScheduleModal = () => {
    setScheduleModal({ isOpen: false, loading: false, student: null, schedule: null, error: '' });
  };

  const downloadAsExcel = () => {
    const rows = filtered.map((r, i) => ({
      '#': i + 1,
      'Student Name': r.studentName || '',
      'Registration ID': r.studentId || '',
      'Reg. Status': r.regStatus || '',
      'Contact No': r.contactNo || '',
      'Course': r.course || '',
      'Batch': r.batchName || '',
      'No. of Dues': r.noOfDues ?? '',
      'Due Amount (Rs)': r.dueAmount !== undefined ? Number(r.dueAmount) : '',
      'Due Date': formatDueDate(r.dueDate),
      'Due Status': r.dueStatus || '',
      'Confirmed Amount (Rs)': r.confirmedAmount !== undefined && r.confirmedAmount !== null ? Number(r.confirmedAmount) : '',
      'Confirm Date to Pay': formatDueDate(r.confirmDateToPay),
      'Follow-ups': r.followupCount ?? 0,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dues');

    const fileName = `dues_${monthFilter || 'all'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const downloadPresenceExcel = () => {
    const data = presenceModal.data;
    if (!data) return;
    const wb = XLSX.utils.book_new();

    // Sheet 1 — This Month Summary
    const summaryRows = (data.students || []).map(s => ({
      'Student Name': s.studentName || '',
      'Reg ID': s.studentId || '',
      'Batch': s.batchName || '',
      'Course': s.course || '',
      'Contact No': s.contactNo || '',
      'Due Status': s.dueStatus || '',
      'Reg Status': s.regStatus || '',
      'Due Amount (Rs)': s.dueAmount !== undefined ? Number(s.dueAmount) : '',
      'Present Days': s.presentDays ?? 0,
      'Present Today': s.todayPresent ? 'Yes' : 'No',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'This Month Summary');

    // Sheet 2 — Today Present
    const todayRows = (data.todayData || []).map(r => ({
      'Floor': r.floor || '',
      'Classroom': r.classroomName || '',
      'Subject': r.subjectName || '',
      'Start Time': r.startTime || '',
      'End Time': r.endTime || '',
      'Student Name': r.studentName || '',
      'Reg ID': r.registrationNumber || '',
      'Course': r.course || '',
      'Due Status': r.dueStatus || '',
      'Due Amount (Rs)': r.dueAmount !== undefined ? Number(r.dueAmount) : '',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(todayRows), 'Today Present');

    // Sheet 3 — Floor Wise
    const floorRows = (data.floorMonthData || []).map(r => ({
      'Floor': r.floor || '',
      'Classroom': r.classroomName || '',
      'Date': r.date || '',
      'Subject': r.subjectName || '',
      'Start Time': r.startTime || '',
      'End Time': r.endTime || '',
      'Student Name': r.studentName || '',
      'Reg ID': r.registrationNumber || '',
      'Due Amount (Rs)': r.dueAmount !== undefined ? Number(r.dueAmount) : '',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(floorRows), 'Floor Wise');

    // Sheet 4 — Course Wise Due
    const courseRows = (data.students || [])
      .slice()
      .sort((a, b) => (b.dueAmount || 0) - (a.dueAmount || 0))
      .map(s => ({
        'Course': s.course || '',
        'Batch': s.batchName || '',
        'Student Name': s.studentName || '',
        'Reg ID': s.studentId || '',
        'Due Status': s.dueStatus || '',
        'Reg Status': s.regStatus || '',
        'Present Days': s.presentDays ?? 0,
        'Due Amount (Rs)': s.dueAmount !== undefined ? Number(s.dueAmount) : '',
      }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(courseRows), 'Course Wise Due');

    XLSX.writeFile(wb, `Presence_${data.month || 'export'}.xlsx`);
  };

  if (loading) {
    return (
      <RecoveryLayout title={pageTitle || 'Dues List'} currentSection={section}>
        <PageLoader />
      </RecoveryLayout>
    );
  }

  return (
    <RecoveryLayout title={pageTitle || 'Dues List'} currentSection={section}>
      <style>{`
        @keyframes recoveryFollowupSlideIn {
          from {
            transform: translateX(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Dues List</h2>
          <div style={{ marginTop: 4, fontSize: 13, color: '#64748b', fontWeight: 500 }}>{titleMonthLabel}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {isAdmin && (
            <>
              <button
                type="button"
                onClick={downloadAsExcel}
                disabled={filtered.length === 0}
                style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', fontSize: 13, fontWeight: 700, cursor: filtered.length === 0 ? 'not-allowed' : 'pointer', opacity: filtered.length === 0 ? 0.5 : 1 }}
              >
                ↓ Download Excel
              </button>
              <button
                type="button"
                onClick={openReceiptImportModal}
                style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                Import Paid Receipts
              </button>
              <button
                type="button"
                onClick={openPresenceModal}
                style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #d0f4de', background: '#f0fdf4', color: '#15803d', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                Check Students Presence
              </button>
            </>
          )}
          <a
            href="/recovery/upload-dues"
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            ↑ Upload Dues
          </a>
        </div>
      </div>

      {receiptImportFeedback && (
        <div style={{
          marginBottom: 18,
          borderRadius: 12,
          border: receiptImportFeedback.type === 'warning' ? '1px solid #fde68a' : '1px solid #bbf7d0',
          background: receiptImportFeedback.type === 'warning' ? '#fffbeb' : '#f0fdf4',
          color: receiptImportFeedback.type === 'warning' ? '#92400e' : '#166534',
          padding: '12px 16px',
          fontSize: 13,
          fontWeight: 600,
        }}>
          {receiptImportFeedback.message}
        </div>
      )}

      {/* Analytics Summary */}
      <div style={{ marginBottom: 20 }}>
        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', padding: '16px 20px', borderLeft: '4px solid #dc2626' }}>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Total Due Amount</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#dc2626', lineHeight: 1 }}>{fmt(summary.totalDueAmount)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{fmtShort(summary.totalDueAmount)} gross outstanding balance</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', padding: '16px 20px', borderLeft: '4px solid #d97706' }}>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Paid Amount</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#d97706', lineHeight: 1 }}>{fmt(summary.actualPaidAmount)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{fmtShort(summary.actualPaidAmount)} from registered students</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', padding: '16px 20px', borderLeft: '4px solid #7c3aed' }}>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Total Paid (incl. Extra)</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed', lineHeight: 1 }}>{fmt(summary.totalPaidAmount)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              {fmtShort(summary.actualPaidAmount)} actual
              {summary.extraPaidAmount > 0 && (
                <span style={{ marginLeft: 6, color: '#7c3aed', fontWeight: 600 }}>+ {fmtShort(summary.extraPaidAmount)} extra</span>
              )}
            </div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', padding: '16px 20px', borderLeft: '4px solid #16a34a' }}>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Confirmed Amount</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#16a34a', lineHeight: 1 }}>{fmt(summary.totalConfirmedAmount)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{fmtShort(summary.totalConfirmedAmount)} committed by students</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', padding: '16px 20px', borderLeft: '4px solid #0369a1' }}>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Today&#39;s Confirmed</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#0369a1', lineHeight: 1 }}>{fmt(summary.todayConfirmedAmount)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{fmtShort(summary.todayConfirmedAmount)} expected to be received today</div>
          </div>
        </div>
        {/* Collection Progress */}
        {(() => {
          const pct = summary.totalDueAmount > 0
            ? Math.min(100, (summary.totalPaidAmount / summary.totalDueAmount) * 100)
            : 0;
          const pctDisplay = pct.toFixed(1);
          const remaining = summary.totalDueAmount - summary.totalPaidAmount;
          const barColor = pct >= 75 ? '#16a34a' : pct >= 40 ? '#d97706' : '#dc2626';
          return (
            <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', padding: '16px 20px', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>Collection Progress</span>
                  <span style={{ marginLeft: 10, fontSize: 12, color: '#94a3b8' }}>Paid vs. Total Due</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 22, fontWeight: 700, color: barColor, lineHeight: 1 }}>{pctDisplay}%</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>collected</span>
                </div>
              </div>
              {/* Bar */}
              <div style={{ height: 10, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, borderRadius: 99, background: barColor, transition: 'width 0.4s ease' }} />
              </div>
              {/* Legend */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  <span style={{ fontWeight: 600, color: barColor }}>{fmtShort(summary.actualPaidAmount)}</span> paid
                  {summary.extraPaidAmount > 0 && (
                    <span style={{ color: '#7c3aed', fontWeight: 600 }}> + {fmtShort(summary.extraPaidAmount)} extra</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  <span style={{ fontWeight: 600, color: '#dc2626' }}>{fmtShort(remaining > 0 ? remaining : 0)}</span> remaining &nbsp;·&nbsp; Total <span style={{ fontWeight: 600, color: '#0f172a' }}>{fmtShort(summary.totalDueAmount)}</span>
                </div>
              </div>
            </div>
          );
        })()}
        {/* Status Breakdown */}
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', padding: '14px 20px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4, whiteSpace: 'nowrap' }}>Status Breakdown</span>
          {/* All pill */}
          {(() => {
            const isActive = statusFilter === 'all';
            return (
              <button
                key="all"
                type="button"
                onClick={() => setStatusFilter('all')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: isActive ? '#0f172a' : '#f1f5f9',
                  borderRadius: 20, padding: '5px 14px',
                  border: isActive ? '2px solid #0f172a' : '2px solid transparent',
                  cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: isActive ? '0 0 0 3px rgba(15,23,42,0.15)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 700, color: isActive ? '#fff' : '#475569' }}>{summary.total}</span>
                <span style={{ fontSize: 12, color: isActive ? '#e2e8f0' : '#475569', fontWeight: 500 }}>All</span>
              </button>
            );
          })()}
          {STATUS_OPTIONS.filter((s) => s !== 'all').map((s) => {
            const sc = STATUS_COLORS[s] || { bg: '#f1f5f9', color: '#475569' };
            const count = summary.statusCounts[s] || 0;
            const isActive = statusFilter === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(isActive ? 'all' : s)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: isActive ? sc.color : sc.bg,
                  borderRadius: 20, padding: '5px 14px',
                  border: isActive ? `2px solid ${sc.color}` : '2px solid transparent',
                  cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: isActive ? `0 0 0 3px ${sc.color}30` : 'none',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? '#fff' : sc.color, display: 'inline-block', flexShrink: 0 }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: isActive ? '#fff' : sc.color }}>{count}</span>
                <span style={{ fontSize: 12, color: isActive ? '#fff' : sc.color, textTransform: 'capitalize', fontWeight: 500 }}>{s.charAt(0).toUpperCase() + s.slice(1)}</span>
              </button>
            );
          })}
        </div>
        {/* Reg. Status Breakdown */}
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', padding: '14px 20px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
          <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4, whiteSpace: 'nowrap' }}>Reg. Status</span>
          {/* All pill */}
          {(() => {
            const isActive = regStatusFilter === 'all';
            return (
              <button
                key="all"
                type="button"
                onClick={() => setRegStatusFilter('all')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: isActive ? '#0f172a' : '#f1f5f9',
                  borderRadius: 20, padding: '5px 14px',
                  border: isActive ? '2px solid #0f172a' : '2px solid transparent',
                  cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: isActive ? '0 0 0 3px rgba(15,23,42,0.15)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 700, color: isActive ? '#fff' : '#475569' }}>{summaryScope.length}</span>
                <span style={{ fontSize: 12, color: isActive ? '#e2e8f0' : '#475569', fontWeight: 500 }}>All</span>
              </button>
            );
          })()}
          {Object.entries(REG_STATUS_COLORS).map(([key, sc]) => {
            const count = summary.regStatusCounts[key] || 0;
            if (count === 0) return null;
            const isActive = regStatusFilter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setRegStatusFilter(isActive ? 'all' : key)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: isActive ? sc.bg : '#f1f5f9',
                  borderRadius: 20, padding: '5px 14px',
                  border: isActive ? `2px solid ${sc.bg}` : '2px solid transparent',
                  cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: isActive ? `0 0 0 3px ${sc.bg}60` : 'none',
                  transition: 'all 0.15s',
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? sc.color : sc.bg, display: 'inline-block', flexShrink: 0 }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: isActive ? sc.color : '#475569' }}>{count}</span>
                <span style={{ fontSize: 12, color: isActive ? sc.color : '#475569', textTransform: 'capitalize', fontWeight: 500 }}>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by name, ID or batch…"
          value={search}
          onChange={handleSearchChange}
          style={{
            flex: '1 1 240px',
            height: 40,
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            padding: '0 12px',
            fontSize: 14,
            outline: 'none',
          }}
        />
        {/* Month filter */}
        <select
          value={monthFilter}
          onChange={handleMonthChange}
          style={{ height: 40, borderRadius: 8, border: '1px solid #e2e8f0', padding: '0 12px', fontSize: 14, background: '#fff', outline: 'none', minWidth: 160 }}
        >
          <option value="">All Months</option>
          {availableMonths.map((m) => (
            <option key={m} value={m}>
              {new Date(m + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={handleStatusChange}
          style={{
            height: 40,
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            padding: '0 12px',
            fontSize: 14,
            background: '#fff',
            outline: 'none',
            textTransform: 'capitalize',
          }}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        {/* Course filter */}
        <div style={{ minWidth: 220, flex: '1 1 220px' }}>
          <Select
            isMulti
            isClearable
            placeholder="All Courses"
            options={courseOptions}
            value={courseFilter}
            onChange={(val) => setCourseFilter(val || [])}
            styles={{
              control: (base, state) => ({
                ...base,
                minHeight: 40,
                borderRadius: 8,
                border: state.isFocused ? '1px solid #94a3b8' : '1px solid #e2e8f0',
                boxShadow: 'none',
                fontSize: 14,
              }),
              valueContainer: (base) => ({ ...base, padding: '2px 10px', flexWrap: 'wrap' }),
              indicatorSeparator: () => ({ display: 'none' }),
              dropdownIndicator: (base) => ({ ...base, padding: '0 6px' }),
              multiValue: (base) => ({ ...base, borderRadius: 6, background: '#eff6ff' }),
              multiValueLabel: (base) => ({ ...base, color: '#1d4ed8', fontWeight: 600, fontSize: 12 }),
              multiValueRemove: (base) => ({ ...base, color: '#1d4ed8', ':hover': { background: '#dbeafe', color: '#1e40af' } }),
            }}
          />
        </div>
        {/* Batch filter */}
        <div style={{ minWidth: 190, flex: '0 0 190px' }}>
          <Select
            isClearable
            placeholder="All Batches"
            options={batchOptions}
            value={batchFilter}
            onChange={setBatchFilter}
            styles={{
              control: (base, state) => ({
                ...base,
                height: 40,
                minHeight: 40,
                borderRadius: 8,
                border: state.isFocused ? '1px solid #94a3b8' : '1px solid #e2e8f0',
                boxShadow: 'none',
                fontSize: 14,
              }),
              valueContainer: (base) => ({ ...base, padding: '0 10px' }),
              indicatorSeparator: () => ({ display: 'none' }),
              dropdownIndicator: (base) => ({ ...base, padding: '0 6px' }),
            }}
          />
        </div>
      </div>

      {/* Date Range filter bar */}
      <div style={{
        background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
        padding: '12px 20px', marginBottom: 12,
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12,
      }}>
        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>Filter by Date</span>
        {/* Toggle */}
        <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <button
            type="button"
            onClick={() => { setDateRangeField('dueDate'); setDateRangeStart(''); setDateRangeEnd(''); }}
            style={{
              height: 36, padding: '0 18px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
              background: dateRangeField === 'dueDate' ? '#dc2626' : '#f8fafc',
              color: dateRangeField === 'dueDate' ? '#fff' : '#64748b',
              transition: 'all 0.15s', borderRight: '1px solid #e2e8f0',
            }}
          >
            Due Date
          </button>
          <button
            type="button"
            onClick={() => { setDateRangeField('confirmDateToPay'); setDateRangeStart(''); setDateRangeEnd(''); }}
            style={{
              height: 36, padding: '0 18px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
              background: dateRangeField === 'confirmDateToPay' ? '#6366f1' : '#f8fafc',
              color: dateRangeField === 'confirmDateToPay' ? '#fff' : '#64748b',
              transition: 'all 0.15s',
            }}
          >
            Confirm Date
          </button>
        </div>
        {/* Date inputs */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="date"
            value={dateRangeStart}
            onChange={(e) => setDateRangeStart(e.target.value)}
            title="From date"
            style={{ height: 36, borderRadius: 8, border: '1px solid #e2e8f0', padding: '0 10px', fontSize: 13, outline: 'none', background: '#f8fafc', minWidth: 140 }}
          />
          <span style={{ fontSize: 13, color: '#94a3b8', fontWeight: 700 }}>—</span>
          <input
            type="date"
            value={dateRangeEnd}
            onChange={(e) => setDateRangeEnd(e.target.value)}
            title="To date"
            style={{ height: 36, borderRadius: 8, border: '1px solid #e2e8f0', padding: '0 10px', fontSize: 13, outline: 'none', background: '#f8fafc', minWidth: 140 }}
          />
        </div>
        {(dateRangeStart || dateRangeEnd) && (
          <button
            type="button"
            onClick={() => { setDateRangeStart(''); setDateRangeEnd(''); }}
            style={{ height: 36, padding: '0 14px', borderRadius: 8, border: 'none', background: '#fee2e2', color: '#dc2626', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5 }}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* Record count */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          Showing <span style={{ fontWeight: 700, color: '#0f172a' }}>{summary.total}</span> {summary.total === 1 ? 'record' : 'records'}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '520px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {['#', 'Student', 'Total Due', 'Due Date', 'Due Status', 'Confirmed Amount', 'Confirm Date to Pay', 'Followup'].map((h) => (
                  <th
                    key={h}
                    style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap', background: '#f8fafc' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8' }}>
                    No dues records found.
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => {
                  const statusStyle = STATUS_COLORS[r.dueStatus] || { bg: '#f1f5f9', color: '#475569' };
                  const isPaid = r.dueStatus === 'paid';
                  return (
                    <tr key={r.dueId ?? i} style={{ borderBottom: '1px solid #e2e8f0', background: isPaid ? '#dcfce7' : 'transparent' }}>
                      <td style={{ padding: '12px 16px', color: '#94a3b8' }}>{i + 1}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{r.studentName || '—'}</div>
                        <div style={{ marginTop: 2, fontSize: 12, color: '#94a3b8' }}>{r.studentId || '—'}</div>
                        {r.regStatus && (() => {
                          const rs = REG_STATUS_COLORS[r.regStatus.toLowerCase()] || { bg: '#475569', color: '#f1f5f9' };
                          return (
                            <span style={{
                              display: 'inline-block', marginTop: 5,
                              background: rs.bg, color: rs.color,
                              borderRadius: 999, padding: '2px 9px',
                              fontSize: 11, fontWeight: 700, textTransform: 'capitalize',
                              letterSpacing: '0.03em',
                            }}>
                              {r.regStatus}
                            </span>
                          );
                        })()}
                        <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>{r.batchName || '—'}</div>
                        <div style={{ marginTop: 4, fontSize: 12, color: '#475569' }}>{r.course || '—'}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                          <button
                            type="button"
                            onClick={() => handleCopyContact(r.contactNo)}
                            disabled={!r.contactNo}
                            title={r.contactNo ? 'Copy without leading zero' : 'No contact number'}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              borderRadius: 999,
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              padding: '4px 10px',
                              fontSize: 12,
                              fontWeight: 600,
                              border: 'none',
                              cursor: r.contactNo ? 'pointer' : 'default',
                              opacity: r.contactNo ? 1 : 0.7,
                            }}
                          >
                            {copiedContact === r.contactNo ? 'Copied' : (r.contactNo || 'No Contact')}
                          </button>
                          <span style={{ display: 'inline-flex', alignItems: 'center', borderRadius: 999, background: '#ecfdf5', color: '#047857', padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>
                            {`Dues ${r.noOfDues ?? '—'}`}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#0f172a', fontWeight: 500 }}>Rs {fmt(r.dueAmount)}</td>
                      <td style={{ padding: '12px 16px', color: '#475569', whiteSpace: 'nowrap' }}>{formatDueDate(r.dueDate)}</td>
                      <td
                        style={{ padding: '12px 16px' }}
                        onDoubleClick={() => startEditingCell(r, 'dueStatus')}
                        title="Double-click to edit"
                      >
                        {isEditingCell(r.dueId, 'dueStatus') ? (
                          <select
                            autoFocus
                            value={editingValue}
                            onChange={(e) => {
                              const value = e.target.value;
                              setEditingValue(value);
                              saveInlineEdit(r, 'dueStatus', value);
                            }}
                            onBlur={() => setTimeout(() => stopEditingCell(), 0)}
                            disabled={isSavingCell(r.dueId, 'dueStatus')}
                            style={{ height: 36, minWidth: 140, borderRadius: 8, border: '1px solid #cbd5e1', padding: '0 10px', fontSize: 13, outline: 'none', background: '#fff' }}
                          >
                            {STATUS_OPTIONS.filter((s) => s !== 'all').map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        ) : (
                          <span style={{
                            display: 'inline-block',
                            borderRadius: 6,
                            padding: '3px 10px',
                            fontSize: 12,
                            fontWeight: 600,
                            background: statusStyle.bg,
                            color: statusStyle.color,
                            textTransform: 'capitalize',
                            cursor: 'pointer',
                          }}>
                            {r.dueStatus || '—'}
                          </span>
                        )}
                      </td>
                      <td
                        style={{ padding: '12px 16px', color: '#0f172a', fontWeight: 500 }}
                        onDoubleClick={() => startEditingCell(r, 'confirmedAmount')}
                        title="Double-click to edit"
                      >
                        {isEditingCell(r.dueId, 'confirmedAmount') ? (
                          <input
                            autoFocus
                            type="number"
                            step="0.01"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={() => saveInlineEdit(r, 'confirmedAmount', editingValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveInlineEdit(r, 'confirmedAmount', editingValue);
                              if (e.key === 'Escape') stopEditingCell();
                            }}
                            disabled={isSavingCell(r.dueId, 'confirmedAmount')}
                            style={{ width: 120, height: 36, borderRadius: 8, border: '1px solid #cbd5e1', padding: '0 10px', fontSize: 13, outline: 'none' }}
                          />
                        ) : (
                          <span style={{ cursor: 'pointer' }}>Rs {fmt(r.confirmedAmount)}</span>
                        )}
                      </td>
                      <td
                        style={{ padding: '12px 16px', color: '#475569', whiteSpace: 'nowrap' }}
                        onDoubleClick={() => startEditingCell(r, 'confirmDateToPay')}
                        title="Double-click to edit"
                      >
                        {isEditingCell(r.dueId, 'confirmDateToPay') ? (
                          <input
                            autoFocus
                            type="date"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={() => saveInlineEdit(r, 'confirmDateToPay', editingValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveInlineEdit(r, 'confirmDateToPay', editingValue);
                              if (e.key === 'Escape') stopEditingCell();
                            }}
                            disabled={isSavingCell(r.dueId, 'confirmDateToPay')}
                            style={{ width: 150, height: 36, borderRadius: 8, border: '1px solid #cbd5e1', padding: '0 10px', fontSize: 13, outline: 'none' }}
                          />
                        ) : (
                          <span style={{ cursor: 'pointer' }}>{formatDueDate(r.confirmDateToPay)}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          onClick={() => openFollowUpModal(r)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            height: 34,
                            padding: '0 14px',
                            borderRadius: 8,
                            border: '1px solid #bfdbfe',
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          <span>Followup</span>
                          <span style={{
                            minWidth: 20,
                            height: 20,
                            borderRadius: 999,
                            background: '#dbeafe',
                            color: '#1e3a8a',
                            fontSize: 11,
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 6px',
                          }}>
                            {r.followupCount ?? 0}
                          </span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

      </div>

      {inlineError && (
        <div style={{ marginTop: 12, borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', fontSize: 13, padding: '10px 12px' }}>
          {inlineError}
        </div>
      )}

      {followUpModal.isOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', justifyContent: 'flex-start' }}>
          <div
            onClick={closeFollowUpModal}
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.52)' }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="recovery-followup-title"
            style={{
              position: 'relative',
              height: '100%',
              width: '100%',
              maxWidth: 440,
              background: '#ffffff',
              boxShadow: '18px 0 48px rgba(15, 23, 42, 0.18)',
              animation: 'recoveryFollowupSlideIn 220ms ease-out',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ padding: '18px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <h3 id="recovery-followup-title" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Followup</h3>
                <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>
                  {followUpModal.row?.studentName || 'Student'}
                  {followUpModal.row?.studentId ? ` • ${followUpModal.row.studentId}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={closeFollowUpModal}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 999,
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  color: '#475569',
                  fontSize: 18,
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: 20, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0', padding: 14, display: 'grid', gap: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>Total Due</div>
                    <div style={{ marginTop: 4, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Rs {fmt(followUpModal.row?.dueAmount)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>Due Status</div>
                    <div style={{ marginTop: 4, fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{followUpModal.row?.dueStatus || '—'}</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>Due Date</div>
                    <div style={{ marginTop: 4, fontSize: 14, color: '#334155' }}>{formatDueDate(followUpModal.row?.dueDate)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>Confirm Date to Pay</div>
                    <div style={{ marginTop: 4, fontSize: 14, color: '#334155' }}>{formatDueDate(followUpModal.row?.confirmDateToPay)}</div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Comment
                  </label>
                  <textarea
                    value={followUpForm.comment}
                    onChange={(e) => setFollowUpForm((prev) => ({ ...prev, comment: e.target.value }))}
                    rows={4}
                    placeholder="Add follow-up comment..."
                    style={{ width: '100%', borderRadius: 10, border: '1px solid #cbd5e1', padding: '10px 12px', fontSize: 14, color: '#0f172a', resize: 'vertical', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Next Reminder Date
                  </label>
                  <input
                    type="date"
                    value={followUpForm.nextReminderDate}
                    onChange={(e) => setFollowUpForm((prev) => ({ ...prev, nextReminderDate: e.target.value }))}
                    style={{ width: '100%', height: 42, borderRadius: 10, border: '1px solid #cbd5e1', padding: '0 12px', fontSize: 14, color: '#0f172a', outline: 'none' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Next Reminder Comment
                  </label>
                  <textarea
                    value={followUpForm.nextReminderComment}
                    onChange={(e) => setFollowUpForm((prev) => ({ ...prev, nextReminderComment: e.target.value }))}
                    rows={2}
                    placeholder="Optional note for the next reminder..."
                    style={{ width: '100%', borderRadius: 10, border: '1px solid #cbd5e1', padding: '10px 12px', fontSize: 14, color: '#0f172a', resize: 'vertical', outline: 'none' }}
                  />
                </div>

                {followUpForm.error && (
                  <div style={{ borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', fontSize: 13, padding: '10px 12px' }}>
                    {followUpForm.error}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={closeFollowUpModal}
                    style={{
                      flex: '0 0 auto',
                      height: 40,
                      padding: '0 16px',
                      borderRadius: 10,
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      color: '#475569',
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={submitFollowUp}
                    disabled={followUpForm.submitting}
                    style={{
                      flex: '1 1 auto',
                      height: 40,
                      padding: '0 16px',
                      borderRadius: 10,
                      border: 'none',
                      background: followUpForm.submitting ? '#93c5fd' : '#2563eb',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: followUpForm.submitting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {followUpForm.submitting ? 'Saving...' : 'Save Followup'}
                  </button>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 }}>
                  Follow-up History
                </div>

                {followUpForm.loadingHistory ? (
                  <div style={{ borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', padding: '14px 12px', fontSize: 13, color: '#64748b' }}>
                    Loading history...
                  </div>
                ) : followUpHistory.length === 0 ? (
                  <div style={{ borderRadius: 10, border: '1px dashed #cbd5e1', background: '#fff', padding: '14px 12px', fontSize: 13, color: '#64748b' }}>
                    No follow-up history yet.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {followUpHistory.map((item) => (
                      <div key={item.id} style={{ borderRadius: 12, border: '1px solid #e2e8f0', background: '#fff', padding: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{item.createdBy || 'Recovery Team'}</div>
                          <div style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDateTime(item.commentDate || item.createdDate)}</div>
                        </div>
                        <div style={{ marginTop: 8, fontSize: 14, color: '#334155', lineHeight: 1.5 }}>{item.comment || '—'}</div>
                        {(item.nextReminderDate || item.nextReminderComment) && (
                          <div style={{ marginTop: 10, borderRadius: 10, background: '#f8fafc', padding: '10px 12px' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Next Reminder</div>
                            <div style={{ marginTop: 4, fontSize: 13, color: '#0f172a' }}>{formatDateTime(item.nextReminderDate)}</div>
                            {item.nextReminderComment ? (
                              <div style={{ marginTop: 4, fontSize: 13, color: '#475569' }}>{item.nextReminderComment}</div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <PaidReceiptsImportModal
        isOpen={receiptImportModal.isOpen}
        onClose={closeReceiptImportModal}
        records={records}
        monthFilter={monthFilter}
        onImported={handlePaidReceiptImportCompleted}
      />

      {/* ── Check Students Presence Modal ── */}
      {presenceModal.isOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={closePresenceModal} aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.62)', backdropFilter: 'blur(2px)' }} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="presence-modal-title"
            style={{ position: 'relative', width: '100%', maxWidth: 980, maxHeight: '92vh', overflow: 'hidden', background: '#f8fafc', borderRadius: 20, boxShadow: '0 32px 80px rgba(15,23,42,0.32)', display: 'flex', flexDirection: 'column' }}
          >
            {/* ── Modal Header + tabs ── */}
            <div style={{ padding: '16px 24px 0', borderBottom: '1px solid #e2e8f0', background: '#fff', borderRadius: '20px 20px 0 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 14 }}>
                <div>
                  <h3 id="presence-modal-title" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Check Students Presence</h3>
                  <div style={{ marginTop: 3, fontSize: 12, color: '#64748b' }}>
                    {formatMonthTitle(presenceModal.data?.month)}
                    {presenceModal.data?.attendanceWindow && (
                      <span> — {presenceModal.data.attendanceWindow.from} to {presenceModal.data.attendanceWindow.to}</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    onClick={downloadPresenceExcel}
                    disabled={!presenceModal.data || presenceModal.loading}
                    style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: (!presenceModal.data || presenceModal.loading) ? 0.5 : 1 }}
                  >
                    ↓ Download Excel
                  </button>
                  <button type="button" onClick={closePresenceModal} style={{ width: 34, height: 34, borderRadius: 999, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
                </div>
              </div>
              {/* Tab bar */}
              <div style={{ display: 'flex', gap: 2 }}>
                {[
                  { key: 'summary', label: 'This Month Summary' },
                  { key: 'today',   label: 'Today Present' },
                  { key: 'floors',  label: 'Floor Wise' },
                  { key: 'courses', label: 'Course Wise Due' },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => { setPresenceTab(tab.key); setExpandedGroups({}); setExpandedSessions({}); setExpandedFloors({}); }}
                    style={{
                      height: 36, padding: '0 18px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                      borderRadius: '8px 8px 0 0',
                      background: presenceTab === tab.key ? '#f8fafc' : 'transparent',
                      color: presenceTab === tab.key ? '#0f172a' : '#64748b',
                      borderBottom: presenceTab === tab.key ? '2px solid #0f172a' : '2px solid transparent',
                      transition: 'all 0.15s',
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Body ── */}
            <div style={{ overflowY: 'auto', flex: 1, padding: 24 }}>
              {presenceModal.loading && <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>Loading presence data…</div>}
              {presenceModal.error && <div style={{ borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', fontSize: 13, padding: '10px 12px' }}>{presenceModal.error}</div>}

              {!presenceModal.loading && !presenceModal.error && presenceModal.data && (() => {
                const students      = presenceModal.data.students      || [];
                const floorMonthData = presenceModal.data.floorMonthData || [];
                const todayData     = presenceModal.data.todayData     || [];

                const presentCount        = students.filter((s) => s.presentDays > 0).length;
                const todayPresentCount   = students.filter((s) => s.todayPresent).length;
                const totalDue            = students.reduce((sum, s) => sum + (s.dueAmount || 0), 0);
                const todayPresentDueTotal = students.filter((s) => s.todayPresent).reduce((sum, s) => sum + (s.dueAmount || 0), 0);
                const cameThisMonthDue    = students.filter((s) => s.presentDays > 0).reduce((sum, s) => sum + (s.dueAmount || 0), 0);
                const noAttendanceDue     = students.filter((s) => s.presentDays === 0).reduce((sum, s) => sum + (s.dueAmount || 0), 0);

                /* ════════════════════════════════════════════════
                   TAB — THIS MONTH SUMMARY
                ════════════════════════════════════════════════ */
                if (presenceTab === 'summary') {
                  return (
                    <>
                      {/* KPI cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
                        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 16px' }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{students.length}</div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Unpaid students</div>
                        </div>
                        <div style={{ background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0', padding: '14px 16px' }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: '#15803d' }}>{presentCount}</div>
                          <div style={{ fontSize: 12, color: '#166534', marginTop: 2 }}>Came this month</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginTop: 4 }}>Rs {fmt(cameThisMonthDue)}</div>
                        </div>
                        <div style={{ background: '#fff7ed', borderRadius: 12, border: '1px solid #fed7aa', padding: '14px 16px' }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: '#ea580c' }}>{todayPresentCount}</div>
                          <div style={{ fontSize: 12, color: '#c2410c', marginTop: 2 }}>Present today</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#ea580c', marginTop: 4 }}>Rs {fmt(todayPresentDueTotal)}</div>
                        </div>
                        <div style={{ background: '#fee2e2', borderRadius: 12, border: '1px solid #fecaca', padding: '14px 16px' }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: '#dc2626' }}>{students.length - presentCount}</div>
                          <div style={{ fontSize: 12, color: '#991b1b', marginTop: 2 }}>No attendance</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginTop: 4 }}>Rs {fmt(noAttendanceDue)}</div>
                        </div>
                        <div style={{ background: '#fef3c7', borderRadius: 12, border: '1px solid #fde68a', padding: '14px 16px' }}>
                          <div style={{ fontSize: 18, fontWeight: 700, color: '#92400e' }}>Rs {fmt(totalDue)}</div>
                          <div style={{ fontSize: 12, color: '#78350f', marginTop: 2 }}>Total outstanding due</div>
                        </div>
                      </div>
                      {/* Table */}
                      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                              {['#', 'Student', 'Due Status', 'Due Amount', 'Present Days'].map((h) => (
                                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {students.map((s, i) => {
                              const statusStyle = STATUS_COLORS[s.dueStatus] || { bg: '#f1f5f9', color: '#475569' };
                              return (
                                <tr key={s.dueId ?? i} style={{ borderBottom: '1px solid #f1f5f9', background: s.todayPresent ? '#fefce8' : 'transparent' }}>
                                  <td style={{ padding: '10px 14px', color: '#94a3b8' }}>{i + 1}</td>
                                  <td style={{ padding: '10px 14px' }}>
                                    <div style={{ fontWeight: 600, color: '#0f172a' }}>
                                      {s.studentName || '—'}
                                      {s.todayPresent && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, background: '#fef9c3', color: '#854d0e', borderRadius: 999, padding: '1px 7px' }}>Today</span>}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{s.studentId || '—'}</div>
                                    {s.regStatus && (() => { const rs = REG_STATUS_COLORS[s.regStatus.toLowerCase()] || { bg: '#475569', color: '#f1f5f9' }; return <span style={{ display: 'inline-block', marginTop: 3, background: rs.bg, color: rs.color, borderRadius: 999, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>{s.regStatus}</span>; })()}
                                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.batchName || ''}{s.batchName && s.course ? ' · ' : ''}{s.course || ''}</div>
                                  </td>
                                  <td style={{ padding: '10px 14px' }}>
                                    <span style={{ display: 'inline-block', borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 600, background: statusStyle.bg, color: statusStyle.color, textTransform: 'capitalize' }}>{s.dueStatus || '—'}</span>
                                  </td>
                                  <td style={{ padding: '10px 14px', fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' }}>Rs {fmt(s.dueAmount)}</td>
                                  <td style={{ padding: '10px 14px' }}>
                                    {s.presentDays > 0 ? (
                                      <button type="button" onClick={() => openScheduleModal(s)}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 30, padding: '0 12px', borderRadius: 20, border: '1px solid #bbf7d0', background: '#dcfce7', color: '#15803d', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                                      >
                                        {s.presentDays} {s.presentDays === 1 ? 'day' : 'days'}
                                      </button>
                                    ) : (
                                      <span style={{ display: 'inline-block', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600, background: '#f1f5f9', color: '#94a3b8' }}>No attendance</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                }

                /* ════════════════════════════════════════════════
                   TAB — TODAY PRESENT
                ════════════════════════════════════════════════ */
                if (presenceTab === 'today') {
                  const todayStudents = students.filter((s) => s.todayPresent);

                  // ── helpers: build unique-student maps from todayData ──
                  const buildStudentMap = (groupKey) => {
                    const groupMap = {};
                    todayData.forEach((row) => {
                      const gk = groupKey(row);
                      if (!groupMap[gk]) groupMap[gk] = new Map();
                      const regId = row.registrationNumber;
                      if (!groupMap[gk].has(regId)) {
                        groupMap[gk].set(regId, { studentName: row.studentName, dueAmount: row.dueAmount, dueStatus: row.dueStatus, regId, _sessKeys: new Set(), sessions: [] });
                      }
                      const st = groupMap[gk].get(regId);
                      const sk = `${row.startTime || ''}|${row.subjectName || ''}|${row.classroomName || ''}`;
                      if (!st._sessKeys.has(sk)) {
                        st._sessKeys.add(sk);
                        st.sessions.push({ subjectName: row.subjectName, classroomName: row.classroomName, startTime: row.startTime, endTime: row.endTime });
                      }
                    });
                    return groupMap;
                  };

                  // ── Normal view: sessions grouped by classroom+time+subject ──
                  const sessionMap = {};
                  todayData.forEach((row) => {
                    const key = `${row.floor}|||${row.classroomName}|||${row.startTime || ''}|||${row.endTime || ''}|||${row.subjectName || ''}`;
                    if (!sessionMap[key]) {
                      sessionMap[key] = { floor: row.floor, classroomName: row.classroomName, startTime: row.startTime, endTime: row.endTime, subjectName: row.subjectName || null, subjectShortName: row.subjectShortName || null, students: new Map() };
                    }
                    if (!sessionMap[key].students.has(row.registrationNumber)) {
                      sessionMap[key].students.set(row.registrationNumber, { studentName: row.studentName, dueAmount: row.dueAmount, dueStatus: row.dueStatus, regId: row.registrationNumber });
                    }
                  });
                  const sessions = Object.values(sessionMap).map((s) => ({ ...s, students: Array.from(s.students.values()).sort((a, b) => (b.dueAmount || 0) - (a.dueAmount || 0)) })).sort((a, b) => b.students.reduce((s, st) => s + (st.dueAmount || 0), 0) - a.students.reduce((s, st) => s + (st.dueAmount || 0), 0));

                  // ── Floor view ──
                  const floorGroupMap = buildStudentMap((r) => r.floor || 'Unknown');
                  const floorGroups = Object.entries(floorGroupMap).map(([fl, stMap]) => {
                    const sts = Array.from(stMap.values()).sort((a, b) => (b.dueAmount || 0) - (a.dueAmount || 0));
                    return { label: fl, students: sts, total: sts.reduce((s, st) => s + (st.dueAmount || 0), 0) };
                  }).sort((a, b) => b.total - a.total);

                  // ── Course view ──
                  const courseGroupMap = buildStudentMap((r) => r.course || 'Unknown');
                  const courseGroups = Object.entries(courseGroupMap).map(([cr, stMap]) => {
                    const sts = Array.from(stMap.values()).sort((a, b) => (b.dueAmount || 0) - (a.dueAmount || 0));
                    return { label: cr, students: sts, total: sts.reduce((s, st) => s + (st.dueAmount || 0), 0) };
                  }).sort((a, b) => b.total - a.total);

                  // ── Reusable grouped student list ──
                  const GroupedList = ({ groups, headerBg, labelPrefix, isDark }) => (
                    <div style={{ display: 'grid', gap: 14 }}>
                      {groups.map((grp, gi) => {
                        const isOpen = !!expandedGroups[gi];
                        return (
                          <div key={gi} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            <div
                              onClick={() => setExpandedGroups((prev) => ({ ...prev, [gi]: !prev[gi] }))}
                              style={{ padding: '12px 18px', background: headerBg || '#f8fafc', borderBottom: isOpen ? '1px solid #e2e8f0' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                            >
                              <div style={{ fontWeight: 700, fontSize: 14, color: isDark ? '#fff' : '#0f172a' }}>{labelPrefix ? `${labelPrefix} ${grp.label}` : grp.label}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#64748b' }}>{grp.students.length} student{grp.students.length !== 1 ? 's' : ''}</div>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#fcd34d' : '#dc2626' }}>Rs {fmt(grp.total)}</div>
                                </div>
                                <span style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#cbd5e1' : '#64748b', background: isDark ? 'rgba(255,255,255,0.12)' : '#e2e8f0', borderRadius: 6, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                                  {isOpen ? 'Hide ▲' : 'View Detail ▾'}
                                </span>
                              </div>
                            </div>
                            {isOpen && (
                              <div>
                                {grp.students.map((s, si) => {
                                  const statusStyle = STATUS_COLORS[s.dueStatus] || { bg: '#f1f5f9', color: '#475569' };
                                  return (
                                    <div key={si} style={{ padding: '10px 18px', borderBottom: si < grp.students.length - 1 ? '1px solid #f8fafc' : 'none', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>
                                          {s.studentName || '—'}
                                          <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{s.regId || ''}</span>
                                        </div>
                                        <div style={{ marginTop: 2 }}>
                                          <span style={{ display: 'inline-block', borderRadius: 6, padding: '1px 8px', fontSize: 11, fontWeight: 600, background: statusStyle.bg, color: statusStyle.color, textTransform: 'capitalize' }}>{s.dueStatus || '—'}</span>
                                        </div>
                                        {s.sessions.length > 0 && (
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                                            {s.sessions.map((sess, sIdx) => (
                                              <span key={sIdx} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 500 }}>
                                                {sess.subjectName && <span style={{ fontWeight: 700 }}>{sess.subjectName}</span>}
                                                {sess.classroomName && <span style={{ color: '#374151' }}>· 🚪 {sess.classroomName}</span>}
                                                {(sess.startTime || sess.endTime) && <span style={{ color: '#4b5563' }}>· {sess.startTime || '—'}{sess.endTime ? ` – ${sess.endTime}` : ''}</span>}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626', whiteSpace: 'nowrap', paddingTop: 1 }}>Rs {fmt(s.dueAmount)}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );

                  return (
                    <>
                      {/* KPI */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: 12, marginBottom: 16 }}>
                        <div style={{ background: '#fff7ed', borderRadius: 12, border: '1px solid #fed7aa', padding: '14px 16px' }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: '#ea580c' }}>{todayStudents.length}</div>
                          <div style={{ fontSize: 12, color: '#c2410c', marginTop: 2 }}>Present today</div>
                        </div>
                        <div style={{ background: '#fef3c7', borderRadius: 12, border: '1px solid #fde68a', padding: '14px 16px' }}>
                          <div style={{ fontSize: 18, fontWeight: 700, color: '#92400e' }}>Rs {fmt(todayPresentDueTotal)}</div>
                          <div style={{ fontSize: 12, color: '#78350f', marginTop: 2 }}>Total due (present today)</div>
                        </div>
                        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 16px' }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: '#64748b' }}>{students.length - todayStudents.length}</div>
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Absent today</div>
                        </div>
                      </div>

                      {/* View dropdown */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>Group by</label>
                        <select
                          value={todayView}
                          onChange={(e) => { setTodayView(e.target.value); setExpandedGroups({}); }}
                          style={{ flex: 1, fontSize: 13, padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#0f172a', cursor: 'pointer', outline: 'none' }}
                        >
                          <option value="normal">Normal (Session wise)</option>
                          <option value="floor">Floor Wise Amount Today</option>
                          <option value="course">Course Wise</option>
                        </select>
                      </div>

                      {todayStudents.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 52, color: '#94a3b8', fontSize: 14 }}>
                          <div style={{ fontSize: 36, marginBottom: 10 }}>🏫</div>
                          No unpaid students have attendance recorded for today.
                        </div>
                      ) : todayView === 'floor' ? (
                        floorGroups.length > 0
                          ? <GroupedList groups={floorGroups} headerBg="#0f172a" labelPrefix="📍" isDark />
                          : <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No floor data available.</div>
                      ) : todayView === 'course' ? (
                        courseGroups.length > 0
                          ? <GroupedList groups={courseGroups} headerBg="#1e3a8a" labelPrefix="🎓" isDark />
                          : <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No course data available.</div>
                      ) : sessions.length > 0 ? (
                        /* Normal: grouped by session (classroom + time + subject) */
                        <div style={{ display: 'grid', gap: 14 }}>
                          {sessions.map((sess, ci) => {
                            const sessionDue = sess.students.reduce((sum, s) => sum + (s.dueAmount || 0), 0);
                            const sessOpen = !!expandedSessions[ci];
                            return (
                              <div key={ci} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                <div
                                  onClick={() => setExpandedSessions((prev) => ({ ...prev, [ci]: !prev[ci] }))}
                                  style={{ padding: '12px 18px', background: '#f8fafc', borderBottom: sessOpen ? '1px solid #e2e8f0' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, cursor: 'pointer', userSelect: 'none' }}
                                >
                                  <div>
                                    {sess.subjectName && (
                                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', marginBottom: 4 }}>📖 {sess.subjectName}</div>
                                    )}
                                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{sess.classroomName}</div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                      {sess.floor && <span style={{ marginRight: 10 }}>📍 {sess.floor}</span>}
                                      {(sess.startTime || sess.endTime) && <span>🕐 {sess.startTime || '—'}{sess.endTime ? ` – ${sess.endTime}` : ''}</span>}
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ textAlign: 'right' }}>
                                      <div style={{ fontSize: 12, color: '#64748b' }}>{sess.students.length} student{sess.students.length !== 1 ? 's' : ''}</div>
                                      <div style={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>Rs {fmt(sessionDue)}</div>
                                    </div>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', background: '#e2e8f0', borderRadius: 6, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                                      {sessOpen ? 'Hide ▲' : 'View Detail ▾'}
                                    </span>
                                  </div>
                                </div>
                                {sessOpen && <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                  <tbody>
                                    {sess.students.map((s, si) => {
                                      const statusStyle = STATUS_COLORS[s.dueStatus] || { bg: '#f1f5f9', color: '#475569' };
                                      return (
                                        <tr key={si} style={{ borderBottom: si < sess.students.length - 1 ? '1px solid #f8fafc' : 'none' }}>
                                          <td style={{ padding: '9px 18px', fontWeight: 600, color: '#0f172a' }}>
                                            {s.studentName || '—'}
                                            <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{s.regId || ''}</span>
                                            {(sess.subjectName || sess.startTime) && (
                                              <div style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                {sess.subjectName && <span style={{ fontSize: 11, fontWeight: 600, color: '#1d4ed8', background: '#eff6ff', borderRadius: 4, padding: '1px 6px' }}>📖 {sess.subjectName}</span>}
                                                {(sess.startTime || sess.endTime) && <span style={{ fontSize: 11, color: '#64748b', background: '#f1f5f9', borderRadius: 4, padding: '1px 6px' }}>🕐 {sess.startTime || '—'}{sess.endTime ? ` – ${sess.endTime}` : ''}</span>}
                                              </div>
                                            )}
                                          </td>
                                          <td style={{ padding: '9px 18px' }}>
                                            <span style={{ display: 'inline-block', borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600, background: statusStyle.bg, color: statusStyle.color, textTransform: 'capitalize' }}>{s.dueStatus || '—'}</span>
                                          </td>
                                          <td style={{ padding: '9px 18px', textAlign: 'right', fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' }}>Rs {fmt(s.dueAmount)}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        /* No classroom/session info — flat list */
                        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {['Student', 'Due Status', 'Due Amount'].map((h) => (
                                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {todayStudents.map((s, si) => {
                                const statusStyle = STATUS_COLORS[s.dueStatus] || { bg: '#f1f5f9', color: '#475569' };
                                return (
                                  <tr key={si} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '10px 14px', fontWeight: 600, color: '#0f172a' }}>
                                      {s.studentName || '—'}
                                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{s.studentId || '—'}</div>
                                    </td>
                                    <td style={{ padding: '10px 14px' }}>
                                      <span style={{ display: 'inline-block', borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600, background: statusStyle.bg, color: statusStyle.color, textTransform: 'capitalize' }}>{s.dueStatus || '—'}</span>
                                    </td>
                                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' }}>Rs {fmt(s.dueAmount)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  );
                }

                /* ════════════════════════════════════════════════
                   TAB — FLOOR WISE
                ════════════════════════════════════════════════ */
                if (presenceTab === 'floors') {
                  // Build floor → student (unique per floor, sessions across all rooms)
                  const floorMap = {}; // floor → Map(regId → student)
                  floorMonthData.forEach((row) => {
                    const fl = row.floor || 'Unknown';
                    if (!floorMap[fl]) floorMap[fl] = new Map();
                    const regId = row.registrationNumber;
                    if (!floorMap[fl].has(regId)) {
                      floorMap[fl].set(regId, { studentName: row.studentName, dueAmount: row.dueAmount, regId, _sessionKeys: new Set(), sessions: [] });
                    }
                    const st = floorMap[fl].get(regId);
                    const sessionKey = `${row.date || ''}|${row.startTime || ''}|${row.subjectName || ''}|${row.classroomName || ''}`;
                    if (!st._sessionKeys.has(sessionKey)) {
                      st._sessionKeys.add(sessionKey);
                      st.sessions.push({ date: row.date, startTime: row.startTime, endTime: row.endTime, subjectName: row.subjectName, classroomName: row.classroomName });
                    }
                  });

                  const floors = Object.entries(floorMap).map(([fl, studentsMap]) => {
                    const students = Array.from(studentsMap.values()).sort((a, b) => (b.dueAmount || 0) - (a.dueAmount || 0));
                    return {
                      floor: fl,
                      totalStudents: students.length,
                      floorDueTotal: students.reduce((s, st) => s + (st.dueAmount || 0), 0),
                      students,
                    };
                  }).sort((a, b) => b.floorDueTotal - a.floorDueTotal);

                  if (floors.length === 0) {
                    return (
                      <div style={{ textAlign: 'center', padding: 52, color: '#94a3b8', fontSize: 14 }}>
                        <div style={{ fontSize: 36, marginBottom: 10 }}>🏢</div>
                        No floor-wise attendance data for this month.
                      </div>
                    );
                  }

                  return (
                    <div style={{ display: 'grid', gap: 18 }}>
                      {floors.map((floorItem, fi) => {
                        const isOpen = !!expandedFloors[fi];
                        return (
                        <div key={fi} style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                          {/* Floor header */}
                          <div
                            onClick={() => setExpandedFloors((prev) => ({ ...prev, [fi]: !prev[fi] }))}
                            style={{ padding: '14px 20px', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, cursor: 'pointer', userSelect: 'none' }}
                          >
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>📍 {floorItem.floor}</div>
                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                              <div style={{ display: 'flex', gap: 20 }}>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Students</div>
                                  <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{floorItem.totalStudents}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Total due</div>
                                  <div style={{ fontSize: 16, fontWeight: 700, color: '#fcd34d' }}>Rs {fmt(floorItem.floorDueTotal)}</div>
                                </div>
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 600, color: '#cbd5e1', background: 'rgba(255,255,255,0.12)', borderRadius: 6, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                                {isOpen ? 'Hide ▲' : 'View Detail ▾'}
                              </span>
                            </div>
                          </div>
                          {/* Student rows */}
                          {isOpen && (
                          <div>
                            {floorItem.students.map((st, si) => (
                              <div key={si} style={{ padding: '10px 20px', borderBottom: si < floorItem.students.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>
                                    {st.studentName || '—'}
                                    <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{st.regId || ''}</span>
                                  </div>
                                  {st.sessions.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                                      {st.sessions.map((sess, sIdx) => (
                                        <span key={sIdx} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 500 }}>
                                          {sess.subjectName && <span style={{ fontWeight: 700 }}>{sess.subjectName}</span>}
                                          {sess.classroomName && <span style={{ color: '#374151' }}>· 🚪 {sess.classroomName}</span>}
                                          {(sess.startTime || sess.endTime) && <span style={{ color: '#4b5563' }}>· {sess.startTime || '—'}{sess.endTime ? ` – ${sess.endTime}` : ''}</span>}
                                          {sess.date && <span style={{ color: '#9ca3af' }}>· {sess.date}</span>}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626', whiteSpace: 'nowrap', paddingTop: 1 }}>Rs {fmt(st.dueAmount)}</div>
                              </div>
                            ))}
                          </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  );
                }

                /* ════════════════════════════════════════════════
                   TAB — COURSE WISE DUE
                ════════════════════════════════════════════════ */
                if (presenceTab === 'courses') {
                  // Build course → batch → students
                  const courseMap = {};
                  students.forEach((s) => {
                    const course = s.course || 'Unknown Course';
                    const batch  = s.batchName || 'Unknown Batch';
                    if (!courseMap[course]) courseMap[course] = {};
                    if (!courseMap[course][batch]) courseMap[course][batch] = [];
                    courseMap[course][batch].push(s);
                  });

                  const courses = Object.entries(courseMap).map(([course, batches]) => {
                    const batchList = Object.entries(batches).map(([batch, sts]) => ({
                      batch,
                      students: sts.slice().sort((a, b) => (b.dueAmount || 0) - (a.dueAmount || 0)),
                      batchDue: sts.reduce((sum, s) => sum + (s.dueAmount || 0), 0),
                    })).sort((a, b) => b.batchDue - a.batchDue);
                    return {
                      course,
                      batches: batchList,
                      totalStudents: batchList.reduce((sum, b) => sum + b.students.length, 0),
                      totalDue: batchList.reduce((sum, b) => sum + b.batchDue, 0),
                    };
                  }).sort((a, b) => b.totalDue - a.totalDue);

                  // KPI
                  const grandTotal = students.reduce((sum, s) => sum + (s.dueAmount || 0), 0);

                  return (
                    <div style={{ display: 'grid', gap: 18 }}>
                      {/* Summary KPI strip */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 16px' }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{courses.length}</div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Courses</div>
                        </div>
                        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 16px' }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{students.length}</div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Unpaid students</div>
                        </div>
                        <div style={{ background: '#fef3c7', borderRadius: 12, border: '1px solid #fde68a', padding: '14px 16px' }}>
                          <div style={{ fontSize: 17, fontWeight: 700, color: '#92400e' }}>Rs {fmt(grandTotal)}</div>
                          <div style={{ fontSize: 12, color: '#78350f', marginTop: 2 }}>Total outstanding due</div>
                        </div>
                      </div>

                      {/* Course cards */}
                      {courses.map((cr, ci) => {
                        const isOpen = !!expandedGroups[`c_${ci}`];
                        return (
                          <div key={ci} style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            {/* Course header */}
                            <div
                              onClick={() => setExpandedGroups((prev) => ({ ...prev, [`c_${ci}`]: !prev[`c_${ci}`] }))}
                              style={{ padding: '14px 20px', background: '#1e3a8a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, cursor: 'pointer', userSelect: 'none' }}
                            >
                              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>🎓 {cr.course}</div>
                              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                                <div style={{ display: 'flex', gap: 20 }}>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: 11, color: '#93c5fd' }}>{cr.batches.length} batch{cr.batches.length !== 1 ? 'es' : ''}</div>
                                    <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>{cr.totalStudents} students</div>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: 11, color: '#93c5fd' }}>Total due</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#fcd34d' }}>Rs {fmt(cr.totalDue)}</div>
                                  </div>
                                </div>
                                <span style={{ fontSize: 11, fontWeight: 600, color: '#cbd5e1', background: 'rgba(255,255,255,0.12)', borderRadius: 6, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                                  {isOpen ? 'Hide ▲' : 'View Detail ▾'}
                                </span>
                              </div>
                            </div>

                            {/* Batch sections */}
                            {isOpen && cr.batches.map((bt, bi) => (
                              <div key={bi} style={{ borderTop: '1px solid #f1f5f9' }}>
                                {/* Batch header */}
                                <div style={{ padding: '10px 20px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                                  <div style={{ fontWeight: 700, fontSize: 13, color: '#334155' }}>📚 {bt.batch}</div>
                                  <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                                    <span style={{ fontSize: 12, color: '#64748b' }}>{bt.students.length} student{bt.students.length !== 1 ? 's' : ''}</span>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>Rs {fmt(bt.batchDue)}</span>
                                  </div>
                                </div>
                                {/* Student rows */}
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                  <tbody>
                                    {bt.students.map((s, si) => {
                                      const statusStyle = STATUS_COLORS[s.dueStatus] || { bg: '#f1f5f9', color: '#475569' };
                                      return (
                                        <tr key={si} style={{ borderBottom: si < bt.students.length - 1 ? '1px solid #f8fafc' : 'none' }}>
                                          <td style={{ padding: '9px 20px', fontWeight: 600, color: '#0f172a' }}>
                                            {s.studentName || '—'}
                                            <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{s.studentId || ''}</span>
                                          </td>
                                          <td style={{ padding: '9px 20px' }}>
                                            <span style={{ display: 'inline-block', borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600, background: statusStyle.bg, color: statusStyle.color, textTransform: 'capitalize' }}>{s.dueStatus || '—'}</span>
                                          </td>
                                          <td style={{ padding: '9px 20px', textAlign: 'right', fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' }}>Rs {fmt(s.dueAmount)}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                return null;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ── Individual Attendance Schedule Modal ── */}
      {scheduleModal.isOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={closeScheduleModal} aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.5)' }} />
          <div
            role="dialog"
            aria-modal="true"
            style={{ position: 'relative', width: '100%', maxWidth: 520, maxHeight: '85vh', overflow: 'hidden', background: '#fff', borderRadius: 20, boxShadow: '0 32px 80px rgba(15,23,42,0.28)', display: 'flex', flexDirection: 'column' }}
          >
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{scheduleModal.student?.studentName || 'Student'}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{scheduleModal.student?.studentId} · {formatMonthTitle(scheduleModal.schedule?.month)}</div>
              </div>
              <button type="button" onClick={closeScheduleModal} style={{ width: 32, height: 32, borderRadius: 999, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
            </div>
            {/* Body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: 20 }}>
              {scheduleModal.loading && (
                <div style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>Loading schedule…</div>
              )}
              {scheduleModal.error && (
                <div style={{ borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#be123c', fontSize: 13, padding: '10px 12px' }}>{scheduleModal.error}</div>
              )}
              {!scheduleModal.loading && scheduleModal.schedule && (() => {
                const days = scheduleModal.schedule.schedule || [];
                if (days.length === 0) {
                  return <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 14 }}>No attendance records found for this month.</div>;
                }
                return (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {days.map((day, i) => {
                      const d = new Date(`${day.date}T00:00:00`);
                      const dayName = d.toLocaleDateString('en-GB', { weekday: 'long' });
                      const dateFmt = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                      const isLate = day.status === 'Late';
                      const borderColor = isLate ? '#fde68a' : '#bbf7d0';
                      const bgColor = isLate ? '#fffbeb' : '#f0fdf4';
                      const dotColor = isLate ? '#d97706' : '#16a34a';
                      const badgeBg = isLate ? '#fef3c7' : '#dcfce7';
                      const badgeColor = isLate ? '#92400e' : '#166534';
                      return (
                        <div key={i} style={{ borderRadius: 12, border: `1px solid ${borderColor}`, background: bgColor, overflow: 'hidden' }}>
                          {/* Date + status row */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{dayName}</div>
                              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{dateFmt}</div>
                            </div>
                            <span style={{ display: 'inline-block', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700, background: badgeBg, color: badgeColor }}>{day.status}</span>
                          </div>
                          {/* Class details */}
                          <div style={{ borderTop: `1px solid ${borderColor}`, padding: '8px 14px 10px', display: 'grid', gap: 5 }}>
                            {day.subjectName && (
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, minWidth: 66, paddingTop: 1 }}>Subject</span>
                                <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                  {day.subjectName}
                                  {day.subjectShortName && day.subjectShortName !== day.subjectName && (
                                    <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: '#64748b', background: '#f1f5f9', borderRadius: 5, padding: '1px 6px' }}>{day.subjectShortName}</span>
                                  )}
                                </span>
                              </div>
                            )}
                            {(day.startTime || day.endTime) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, minWidth: 66 }}>Time</span>
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                                  {day.startTime || '—'}{day.endTime ? ` – ${day.endTime}` : ''}
                                </span>
                              </div>
                            )}
                            {day.classroomName && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, minWidth: 66 }}>Classroom</span>
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                                  {day.classroomName}
                                  {day.classroomFloor && (
                                    <span style={{ marginLeft: 6, fontSize: 11, color: '#94a3b8' }}>({day.classroomFloor})</span>
                                  )}
                                </span>
                              </div>
                            )}
                            {day.remarks && (
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, minWidth: 66, paddingTop: 1 }}>Remarks</span>
                                <span style={{ fontSize: 12, color: '#475569', fontStyle: 'italic' }}>{day.remarks}</span>
                              </div>
                            )}
                            {!day.subjectName && !day.startTime && !day.classroomName && (
                              <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>No class details recorded for this session.</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </RecoveryLayout>
  );
}
